import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Badge } from '../components/ui/badge';
import { Input } from '../components/ui/input';
import { roomsApi } from '../services/api';
import { Search, Clock, Award, Loader2 } from 'lucide-react';
import Navbar from '../components/Navbar';
import Footer from '../components/Footer';

const Rooms = () => {
  const navigate = useNavigate();
  const [searchTerm, setSearchTerm] = useState('');
  const [selectedDifficulty, setSelectedDifficulty] = useState('All');
  const [rooms, setRooms] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  const difficulties = ['All', 'Easy', 'Medium', 'Hard', 'Insane'];

  useEffect(() => {
    const fetchRooms = async () => {
      try {
        setLoading(true);
        const data = await roomsApi.getAll();
        setRooms(data);
      } catch (err) {
        console.error('Error fetching rooms:', err);
        setError('Failed to load rooms');
      } finally {
        setLoading(false);
      }
    };
    fetchRooms();
  }, []);

  const filteredRooms = rooms.filter(room => {
    const matchesSearch = room.title.toLowerCase().includes(searchTerm.toLowerCase()) ||
                         room.description.toLowerCase().includes(searchTerm.toLowerCase());
    const matchesDifficulty = selectedDifficulty === 'All' || room.difficulty === selectedDifficulty;
    return matchesSearch && matchesDifficulty;
  });

  const getDifficultyColor = (difficulty) => {
    switch(difficulty) {
      case 'Easy': return '#00FF00';
      case 'Medium': return '#FF9500';
      case 'Hard': return '#FF0055';
      case 'Insane': return '#9D00FF';
      default: return '#00BFFF';
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-[#0A0A0A] text-white flex items-center justify-center">
        <Loader2 className="w-8 h-8 animate-spin text-[#00FF00]" />
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-[#0A0A0A] text-white">
      <Navbar />
      
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8 mt-16">
        <div className="mb-8">
          <h1 className="text-4xl font-bold mb-3">Training Rooms</h1>
          <p className="text-gray-400 text-lg">Hands-on cybersecurity challenges and labs</p>
        </div>

        {/* Filters */}
        <div className="flex flex-col md:flex-row gap-4 mb-8">
          <div className="flex-1 relative">
            <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 w-5 h-5 text-gray-400" />
            <Input 
              placeholder="Search rooms..."
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              className="pl-10 bg-[#1A1A1A] border-gray-800 text-white placeholder:text-gray-500"
            />
          </div>
          <div className="flex gap-2 flex-wrap">
            {difficulties.map((difficulty) => (
              <Button
                key={difficulty}
                variant={selectedDifficulty === difficulty ? 'default' : 'outline'}
                onClick={() => setSelectedDifficulty(difficulty)}
                className={`${
                  selectedDifficulty === difficulty
                    ? 'bg-[#00FF00] text-black hover:bg-[#00DD00]'
                    : 'border-gray-700 text-gray-300 hover:bg-gray-800'
                }`}
              >
                {difficulty}
              </Button>
            ))}
          </div>
        </div>

        {error && (
          <div className="text-center py-12">
            <p className="text-red-400">{error}</p>
          </div>
        )}

        {/* Rooms Grid */}
        <div className="grid md:grid-cols-2 gap-6">
          {filteredRooms.map((room) => (
            <Card 
              key={room.id}
              className="bg-[#1A1A1A] border-gray-800 hover:border-[#00BFFF]/50 transition-all duration-300 hover:transform hover:scale-[1.02] cursor-pointer"
              onClick={() => navigate(`/room/${room.id}`)}
            >
              <CardHeader>
                <div className="flex items-start justify-between mb-2">
                  <CardTitle className="text-xl text-white">{room.title}</CardTitle>
                  <Badge 
                    style={{ 
                      backgroundColor: `${getDifficultyColor(room.difficulty)}20`,
                      color: getDifficultyColor(room.difficulty),
                      borderColor: getDifficultyColor(room.difficulty)
                    }}
                  >
                    {room.difficulty}
                  </Badge>
                </div>
                <CardDescription className="text-gray-400">{room.description}</CardDescription>
              </CardHeader>
              <CardContent>
                <div className="flex items-center justify-between">
                  <div className="flex items-center gap-4 text-sm text-gray-400">
                    <div className="flex items-center gap-1">
                      <Clock className="w-4 h-4" />
                      <span>{room.timeEstimate || '30 min'}</span>
                    </div>
                    <div className="flex items-center gap-1">
                      <Award className="w-4 h-4 text-[#00FF00]" />
                      <span className="text-[#00FF00]">{room.points} pts</span>
                    </div>
                  </div>
                  <Badge variant="outline" className="border-gray-700 text-gray-300">
                    {room.type || 'Challenge'}
                  </Badge>
                </div>
              </CardContent>
            </Card>
          ))}
        </div>

        {filteredRooms.length === 0 && !error && (
          <div className="text-center py-12">
            <p className="text-gray-400 text-lg">No rooms found matching your criteria</p>
          </div>
        )}
      </div>
      <Footer />
    </div>
  );
};

export default Rooms;
